import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
import React, { useContext, useEffect } from 'react';
import { FormFieldProps } from 'lib/forms/FormFieldProps';
import { fieldFactoryComponent } from 'temp/formFactory';
import classNames from 'classnames';
import { FormsContext } from 'lib/forms/FormContext';
import { BodyCopy } from 'src/helpers/BodyCopy';
import { FormikValues, useFormikContext } from 'formik';
import { FormsConstants } from 'lib/constants/forms-constants';
import { useRouter } from 'next/router';
import { fieldPersonalizationPlugin } from 'lib/forms/field-personalization';
import { useAffiliate } from 'lib/context/AffiliateContext';
import { getEnum } from 'lib/utils/get-enum';

export type PageProps = Feature.EnterpriseWeb.Enterprise.Forms.Structure.Page &
  FormFieldProps & {
    classes: string;
  };

type FormLayout = 'Full Width' | 'Two Columns';
type LayoutColumn = 'left' | 'right';

const Page = (props: PageProps) => {
  const { isErrorOnSubmit, setIsErrorOnSubmit } = useContext(FormsContext);
  const { formProps } = useContext(FormsContext);
  const { values, setFieldValue, resetForm, initialValues } = useFormikContext<FormikValues>();

  const { userAffiliate } = useAffiliate();
  props = fieldPersonalizationPlugin.exec(props, userAffiliate);

  const paddingGap =
    formProps.fields?.inputPadding?.fields?.Value?.value === 'reduced'
      ? 'gap-x-s gap-y-xxs'
      : 'gap-y-m gap-x-s';

  const router = useRouter();

  const formLayout = getEnum<FormLayout>(props.fields?.formLayout) || 'Full Width';

  const leftChildren: FormFieldProps[] = [];
  const rightChildren: FormFieldProps[] = [];

  if (formLayout === 'Two Columns') {
    props.fields?.children?.forEach((child: FormFieldProps) => {
      const column = getEnum<LayoutColumn>(child.fields?.column);

      if (column === 'right') {
        rightChildren.push(child);
      } else {
        leftChildren.push(child);
      }
    });
  }

  useEffect(() => {
    const handleRouteChange = () => {
      // Reset the form when the route changes (either first load or we go back)
      resetForm({
        values: {
          ...initialValues, // Keep init values
        },
        errors: {}, // Clear errors
        touched: {}, // Clear touched
      });
      setIsErrorOnSubmit(false);
    };

    router?.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router?.events.off('routeChangeComplete', handleRouteChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router?.asPath]);

  useEffect(() => {
    function handleOpenFormEvent(e: CustomEvent) {
      if (!!e.detail?.form) {
        Object.keys(e.detail.form).forEach((key) => {
          if (key in values) {
            // Only set field value if the field exists
            setFieldValue(key, e.detail.form[key]);
          }
        });
      }
    }
    document.addEventListener(FormsConstants.Enterprise.openFormEvent, handleOpenFormEvent);
    return () => {
      document.removeEventListener(FormsConstants.Enterprise.openFormEvent, handleOpenFormEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!props.fields) {
    return <></>;
  }

  if (formLayout === 'Full Width') {
    return (
      <div className={classNames(props.classes, 'grid grid-cols-12', paddingGap)}>
        {props.fields.children.map((child: FormFieldProps) => fieldFactoryComponent(child))}

        {isErrorOnSubmit && (
          <BodyCopy
            fields={{ body: { value: isErrorOnSubmit } }}
            classes="relative mb-s col-span-12 text-error-outline text-small"
          />
        )}
      </div>
    );
  }

  const leftColumn = (
    <div className={classNames('grid grid-cols-12 content-start', paddingGap)}>
      {leftChildren.map((child: FormFieldProps) => fieldFactoryComponent(child))}

      {isErrorOnSubmit && (
        <BodyCopy
          fields={{ body: { value: isErrorOnSubmit } }}
          classes="relative mb-s col-span-12 text-error-outline text-small"
        />
      )}
    </div>
  );

  const rightColumn = (
    <div className={classNames('grid grid-cols-12 content-start', paddingGap)}>
      {rightChildren.map((child: FormFieldProps) => fieldFactoryComponent(child))}
    </div>
  );

  return (
    <div
      className={classNames(props.classes, 'grid grid-cols-1 gap-y-m ml:grid-cols-2 ml:gap-x-xl')}
    >
      {leftColumn}
      {rightColumn}
    </div>
  );
};

export default Page;
